import TextForm from '../../../components/TextForm/TextForm';
import PushTip from '../../../components/UI/PushTip/PushTip';
import { TelegramPreview, MAX_TEXT_LENGTH } from './PaymentTexts';

// Espelha src/telegram/presenters/local_templates.py (+ os botões de card_texts).
// Forma de pagamento nova = uma entrada aqui (vira mais uma aba).
export const METHODS = [
    {
        key: 'spei', label: 'SPEI', currency: 'MXN', flag: 'mx', title: 'Mensagem do SPEI (México)',
        when: 'Enviada no chat com a CLABE para o comprador transferir.',
        variables: ['plano', 'duration', 'valor', 'clabe', 'banco', 'beneficiario', 'referencia'],
        tip: <>A variável <strong>{'{clabe}'}</strong> é obrigatória: é para onde o comprador transfere.</>,
        buttons: [['spei_copy_clabe', 'Copiar CLABE'], ['spei_copy_amount', 'Copiar monto']],
        sample: {
            plano: 'VIP', duration: 'Mensual', valor: '$499.00 MXN', clabe: '<code>646180157000000004</code>',
            banco: 'STP (Sistema de Transferencia y Pagos)', beneficiario: 'XPag Pagamentos', referencia: 'REF123456',
        },
    },
    {
        key: 'nequi', label: 'Nequi', currency: 'COP', flag: 'co', title: 'Mensagem do Nequi (Colômbia)',
        when: 'Vai como legenda da foto do QR Code.',
        variables: ['plano', 'duration', 'valor', 'codigo', 'validade'],
        tip: <>Até 900 caracteres. <strong>{'{codigo}'}</strong> é opcional: o QR já tem o pagamento.</>,
        buttons: [['nequi_copy', 'Copiar código']],
        sample: { plano: 'VIP', duration: 'Mensual', valor: '$114.520 COP', codigo: '<code>000201010212…</code>', validade: '45' },
        photo: true,
    },
];

const ALLOWED_TAGS = /&lt;(\/?)(b|strong|i|em|u|s|del|code|pre|blockquote)&gt;/g;

/** Mensagem com o exemplo no lugar das variáveis, só com as tags do Telegram. */
function previewHtml(template, sample) {
    const escaped = String(template || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(ALLOWED_TAGS, '<$1$2>');
    return escaped.replace(/\{(\w+)\}/g, (all, name) => (name in sample ? sample[name] : all));
}

/** Botão com liga/desliga e texto (desligado = não vai no Telegram). */
function ButtonRow({ id, label, on, text, placeholder, onToggle, onText }) {
    return (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10">
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input type="checkbox" role="switch" className="sr-only peer" aria-label={`Botão ${label}`}
                    checked={on} onChange={(e) => onToggle(id, e.target.checked)} />
                <div className="w-9 h-5 bg-gray-600 rounded-full peer peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500" />
            </label>
            <input type="text" className="input3 disabled:opacity-40" aria-label={`Texto do botão ${label}`}
                maxLength={MAX_TEXT_LENGTH} disabled={!on} placeholder={placeholder}
                value={text || ''} onChange={(e) => onText(id, e.target.value)} />
        </div>
    );
}

/**
 * Uma forma de pagamento local (SPEI, Nequi...): a mensagem livre (como a do
 * PIX), os botões (liga/desliga + texto) e a prévia. Linha com variável vazia
 * (ex.: banco que não veio) some sozinha no Telegram.
 */
export default function MethodEditor({ method, template, defaultTemplate, onTemplateChange,
    texts, textDefaults, onTextsChange, buttonsOff, onButtonsOffChange }) {
    const off = new Set(buttonsOff);
    const shownButtons = method.buttons
        .filter(([key]) => !off.has(key))
        .map(([key]) => (texts[key] && texts[key].trim()) || textDefaults[key] || '');

    const toggle = (key, on) => {
        const next = new Set(off);
        if (on) next.delete(key); else next.add(key);
        onButtonsOffChange([...buttonsOff.filter((k) => next.has(k)), ...[...next].filter((k) => !buttonsOff.includes(k))]);
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-3">
                <p className="spt-hint mb-0">{method.when}</p>
                {template !== defaultTemplate && (
                    <button type="button" className="shrink-0 text-[11px] text-white/50 hover:text-white underline"
                        onClick={() => onTemplateChange(defaultTemplate)}>
                        Voltar ao padrão
                    </button>
                )}
            </div>

            <TextForm key={method.key} value={template} onChange={onTemplateChange} variables={method.variables} />
            <PushTip>{method.tip}</PushTip>

            <div className="mt-4 grid gap-4 md:grid-cols-2 items-start">
                <div className="flex flex-col gap-2 min-w-0">
                    <p className="text-[11px] text-white/40">Botões</p>
                    {method.buttons.map(([key, label]) => (
                        <ButtonRow key={key} id={key} label={label} on={!off.has(key)} text={texts[key]}
                            placeholder={textDefaults[key]} onToggle={toggle}
                            onText={(k, value) => onTextsChange({ ...texts, [k]: value })} />
                    ))}
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] text-white/40 mb-1.5">Prévia</p>
                    {method.photo && (
                        <div className="mb-1 h-24 rounded-lg bg-white/[0.06] flex items-center justify-center text-[11px] text-white/40">
                            QR Code
                        </div>
                    )}
                    <TelegramPreview html={previewHtml(template, method.sample)} buttons={shownButtons} />
                </div>
            </div>
        </div>
    );
}
